import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ListGroup, Button, Spinner } from 'react-bootstrap';
import {
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaFilePdf,
} from 'react-icons/fa';
import './DocumentList.css';
import ContentUnitTag from '../common/ContentUnitTag';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { usePaginatedTenantList } from '../../hooks/usePaginatedTenantList';
import { LAST_READ_MODULES } from '../../platform/lastRead/lastReadModules';
import { useUnreadItemIds } from '../../platform/lastRead/useUnreadItemIds';
import {
  DOCUMENTS_PAGE_SIZE,
  buildDocumentsPageQuery,
  deleteDocument,
  fetchDocumentsPage,
} from '../../services/documentsService';
import Document from '../../models/Document';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import { fetchUnitNameMap } from '../../services/unitsService';
import {
  PREMIUM_MODULE_IDS,
  premiumListClassName,
  showListNavigateHint,
} from '../../platform/contentListVisual';
import ListItemNavigateHint from '../../platform/ListItemNavigateHint';

async function fetchDocumentsPageItems(user, tenantId, memberships, options) {
  const { documents, lastVisible } = await fetchDocumentsPage(user, tenantId, memberships, options);
  return { items: documents, lastVisible };
}

function DocumentList({ onSelectDocument, isAdmin, onEditDocument }) {
  const [unitNames, setUnitNames] = useState({});

  const listTopRef = useRef(null);

  const { user, loading } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();

  const {
    items: documents,
    pageIndex,
    hasNextPage,
    pageLoading,
    goToPrevPage,
    goToNextPage,
    pageSize,
  } = usePaginatedTenantList({
    tenantId,
    user,
    memberships,
    userLoading: loading,
    pageSize: DOCUMENTS_PAGE_SIZE,
    buildPageQuery: buildDocumentsPageQuery,
    fetchPage: fetchDocumentsPageItems,
    mapDoc: Document.fromFirestore,
    logLabel: 'documents',
  });

  const unreadDocumentIds = useUnreadItemIds(LAST_READ_MODULES.DOCUMENTS, documents);

  const scrollListIntoView = useCallback(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    fetchUnitNameMap(tenantId).then(setUnitNames);
  }, [tenantId]);

  const confirm = useConfirm();
  const toast = useToast();

  const handleDelete = async (documentItem) => {
    const confirmed = await confirm({
      title: 'Izbriši dokument',
      message: 'Ali ste prepričani, da želite izbrisati ta dokument?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteDocument(documentItem);
      toast.deleted('Dokument je bil izbrisan.');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Dokumenta ni bilo mogoče izbrisati.');
    }
  };

  const handlePrevPage = () => {
    if (goToPrevPage()) {
      scrollListIntoView();
    }
  };

  const handleNextPage = () => {
    if (goToNextPage()) {
      scrollListIntoView();
    }
  };

  const formatDate = (date) =>
    date?.toLocaleString('sl-SI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? '';

  const rangeStart = documents.length ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = pageIndex * pageSize + documents.length;
  const showPagination = pageIndex > 0 || hasNextPage;
  const showNavigateHint = showListNavigateHint(PREMIUM_MODULE_IDS.DOCUMENTS);
  const listClassName = premiumListClassName(
    PREMIUM_MODULE_IDS.DOCUMENTS,
    'document-list platform-content-list',
  );
  const wrapperClassName = [
    'document-list-wrapper',
    'platform-content-list-wrapper',
    showNavigateHint && 'platform-content-list-wrapper--card-stack',
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className="platform-content-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  if (!pageLoading && !documents.length && pageIndex === 0) {
    return (
      <div className={`platform-content-list-empty${showNavigateHint ? ' platform-content-list-empty--premium' : ''}`}>
        <strong>Ni dokumentov</strong>
        <span>Trenutno ni objavljenih dokumentov za prikaz.</span>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} ref={listTopRef}>
      {pageLoading ? (
        <div className="platform-content-list-loading platform-content-list-loading--overlay">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Nalaganje...</span>
          </Spinner>
        </div>
      ) : (
        <ListGroup className={listClassName}>
          {documents.map((documentItem) => {
            const isUnread = unreadDocumentIds.includes(documentItem.id);

            return (
              <ListGroup.Item
                key={documentItem.id}
                className={`document-list-item platform-content-list-item${isUnread ? ' document-list-item--unread platform-content-list-item--unread' : ''}`}
                onClick={() => onSelectDocument(documentItem)}
              >
                <div
                  className={`document-list-item-main platform-content-list-item-main${isAdmin || showNavigateHint ? ' document-list-item-main--has-actions platform-content-list-item-main--has-actions' : ''}`}
                >
                  <div className="document-list-item-content platform-content-list-item-content">
                    <div className="platform-content-list-item-thumb platform-content-list-item-thumb--icon">
                      {isUnread && (
                        <span className="platform-content-list-item-unread-badge">
                          <span
                            className="platform-unread-dot"
                            title="Neprebrano"
                            aria-label="Neprebrano"
                          />
                        </span>
                      )}
                      <div className="platform-content-list-item-thumb-placeholder" aria-hidden="true">
                        <FaFilePdf />
                      </div>
                    </div>

                    <div className="document-list-item-body platform-content-list-item-body">
                      <div className="document-list-item-title platform-content-list-item-title">
                        {documentItem.title}
                      </div>
                      <div className="platform-content-list-item-badges">
                        <ContentUnitTag item={documentItem} unitNames={unitNames} />
                      </div>
                      {documentItem.description && (
                        <div className="document-list-item-description platform-content-list-item-excerpt">
                          {documentItem.description}
                        </div>
                      )}
                      <div className="document-list-item-footer platform-content-list-item-footer">
                        <span>Ustvaril {documentItem.displayName || 'Anonimen'}</span>
                        <span className="document-list-item-separator">·</span>
                        <span>{formatDate(documentItem.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {(isAdmin || showNavigateHint) && (
                    <div className="document-list-item-aside platform-content-list-item-aside">
                      <div className="platform-content-list-item-aside-inner">
                        {isAdmin ? (
                          <div className="document-list-item-actions platform-content-list-item-actions">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditDocument(documentItem);
                              }}
                            >
                              <FaEdit /> Uredi
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(documentItem);
                              }}
                            >
                              <FaTrash /> Izbriši
                            </Button>
                          </div>
                        ) : null}
                        {showNavigateHint ? <ListItemNavigateHint /> : null}
                      </div>
                    </div>
                  )}
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      {showPagination && (
        <nav className="platform-content-list-pagination" aria-label="Strani dokumentov">
          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handlePrevPage}
            disabled={pageIndex === 0 || pageLoading}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>Prejšnja</span>
          </Button>

          <div className="platform-content-list-pagination__info">
            <span className="platform-content-list-pagination__page">Stran {pageIndex + 1}</span>
            {documents.length > 0 && (
              <span className="platform-content-list-pagination__range">
                Prikazano {rangeStart}–{rangeEnd}
              </span>
            )}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handleNextPage}
            disabled={!hasNextPage || pageLoading}
          >
            <span>Naslednja</span>
            <FaChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </div>
  );
}

DocumentList.defaultProps = {
  onEditDocument: () => {},
};

export default DocumentList;
